import { formatIsoDate as formatDate } from '../../utils/formatDate';
import { formatMetricValue as formatSharedMetricValue } from '../../utils/formatMetric';

function startOfIsoWeek(year, week) {
  const simple = new Date(Date.UTC(year, 0, 1 + (week - 1) * 7));
  const day = simple.getUTCDay() || 7;
  if (day <= 4) {
    simple.setUTCDate(simple.getUTCDate() - day + 1);
  } else {
    simple.setUTCDate(simple.getUTCDate() + 8 - day);
  }
  return simple;
}

function getIsoWeek(date) {
  const working = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()));
  working.setUTCDate(working.getUTCDate() + 4 - (working.getUTCDay() || 7));
  const yearStart = new Date(Date.UTC(working.getUTCFullYear(), 0, 1));
  return Math.ceil((((working - yearStart) / 86400000) + 1) / 7);
}

function normalizePeriodStartValue(value) {
  if (!value) {
    return '';
  }

  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return formatDate(value);
  }

  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) {
      return '';
    }

    const leadingDate = trimmed.match(/^(\d{4}-\d{2}-\d{2})/);
    if (leadingDate) {
      return leadingDate[1];
    }

    const parsed = new Date(trimmed);
    if (!Number.isNaN(parsed.getTime())) {
      return formatDate(parsed);
    }
  }

  return String(value);
}

function createDefaultPerformanceContext() {
  const now = new Date();
  return {
    locationCode: '',
    periodType: 'monthly',
    year: String(now.getUTCFullYear()),
    month: String(now.getUTCMonth() + 1),
    quarter: String(Math.floor(now.getUTCMonth() / 3) + 1),
    week: String(getIsoWeek(now))
  };
}

function derivePeriodStartFromContext(context = {}) {
  const periodType = context.periodType || 'weekly';
  const year = Number(context.year);
  if (!Number.isFinite(year)) {
    return '';
  }

  if (periodType === 'weekly') {
    const week = Number(context.week);
    return Number.isFinite(week) ? formatDate(startOfIsoWeek(year, week)) : '';
  }

  if (periodType === 'monthly') {
    const month = Number(context.month);
    return Number.isFinite(month)
      ? formatDate(new Date(Date.UTC(year, month - 1, 1)))
      : '';
  }

  if (periodType === 'quarterly') {
    const quarter = Number(context.quarter);
    return Number.isFinite(quarter)
      ? formatDate(new Date(Date.UTC(year, (quarter - 1) * 3, 1)))
      : '';
  }

  return `${year}-01-01`;
}

function alignPerformanceContextPeriod(context = {}, period = '') {
  const periodType = ['weekly', 'monthly', 'quarterly', 'yearly'].includes(period)
    ? period
    : (context.periodType || 'monthly');

  if (context.periodType === periodType) {
    return context;
  }

  return {
    ...context,
    periodType
  };
}

function formatMetricValue(value, unit = '', formatType = 'number') {
  return formatSharedMetricValue(value, unit, 'No data', formatType);
}

function getAggregationMode(kpi = {}) {
  const joined = `${kpi.kpiKey || ''} ${kpi.label || ''}`.toLowerCase();
  const formatType = String(kpi.formatType || '').toLowerCase();
  const unit = String(kpi.unit || '').toLowerCase();
  const kpiKey = String(kpi.kpiKey || '').trim();

  if ([
    'laborEfficiency',
    'serviceResponseTime',
    'customerSatisfaction',
    'qualityScore',
    'attendanceReliability',
    'teamProductivity',
    'goalCompletionRate'
  ].includes(kpiKey)) {
    return 'average';
  }

  if (formatType === 'percent' || formatType === 'points' || unit === '%' || unit === 'pts') {
    return 'average';
  }

  if (joined.includes('per ') || joined.includes('cycle time') || joined.includes('cost per') || joined.includes('share') || joined.includes('utilization') || joined.includes('advocacy') || joined.includes('readiness') || joined.includes('completion') || joined.includes('coverage') || joined.includes('compliance')) {
    return 'average';
  }

  if (joined.includes('sales') || joined.includes('revenue')) {
    return 'sum';
  }

  return formatType === 'number' ? 'sum' : 'average';
}

function getVisiblePerformanceKpis(kpis = [], context = {}) {
  return kpis
    .filter(kpi => {
      if (!kpi.isActive) {
        return false;
      }

      if (context.periodType === 'weekly') {
        return kpi.supportsWeeklyGoal !== false;
      }

      if (context.periodType === 'monthly' || context.periodType === 'quarterly') {
        return kpi.supportsMonthlyGoal !== false;
      }

      if (context.periodType === 'yearly') {
        return kpi.supportsYearlyGoal !== false;
      }

      return true;
    })
    .sort((left, right) => (
      Number(left.sortOrder || 0) - Number(right.sortOrder || 0)
      || String(left.category || '').localeCompare(String(right.category || ''))
      || String(left.sectionName || left.section || '').localeCompare(String(right.sectionName || right.section || ''))
      || String(left.label || left.name || '').localeCompare(String(right.label || right.name || ''))
    ));
}

function inferContextYear(context = {}) {
  const explicitYear = Number(context.year);
  if (Number.isFinite(explicitYear)) {
    return explicitYear;
  }

  if (context.periodStart) {
    const normalizedPeriodStart = normalizePeriodStartValue(context.periodStart);
    const date = new Date(`${normalizedPeriodStart}T00:00:00Z`);
    if (!Number.isNaN(date.getTime())) {
      return date.getUTCFullYear();
    }
  }

  return null;
}

function matchesContextLocation(goal, context = {}) {
  if (!context.locationCode) {
    return true;
  }

  return goal.locationCode === context.locationCode;
}

function isCurrentActiveGoal(goal) {
  return String(goal?.goalStatus || '').toLowerCase() === 'active';
}

function compareCurrentGoalPriority(left, right) {
  return (
    Number(right?.revisionNumber || 0) - Number(left?.revisionNumber || 0)
    || String(right?.updatedAt || '').localeCompare(String(left?.updatedAt || ''))
    || Number(right?.id || 0) - Number(left?.id || 0)
  );
}

function resolveExactGoalRecord(goals = [], kpiKey, context = {}) {
  const periodType = context.periodType || null;
  const periodStart = normalizePeriodStartValue(context.periodStart || null);
  const contextYear = inferContextYear(context);

  const exactMatches = goals.filter(goal => {
    if (goal.kpiKey !== kpiKey) {
      return false;
    }

    if (!isCurrentActiveGoal(goal)) {
      return false;
    }

    if (!matchesContextLocation(goal, context)) {
      return false;
    }

    if (periodType && goal.goalPeriodType !== periodType) {
      return false;
    }

    if (periodStart && normalizePeriodStartValue(goal.periodStart) !== periodStart) {
      return false;
    }

    if (!periodStart && contextYear !== null && Number(goal.goalYear) !== contextYear) {
      return false;
    }

    return true;
  });

  if (exactMatches.length) {
    return [...exactMatches].sort(compareCurrentGoalPriority)[0];
  }

  // Second pass: match by year + sub-period fields when periodStart string comparison fails
  // (e.g. context has month/week/quarter numbers but goal.periodStart is stored differently)
  if (contextYear !== null && periodType) {
    const fallbackMatches = goals.filter(goal => {
      if (goal.kpiKey !== kpiKey) {
        return false;
      }

      if (!isCurrentActiveGoal(goal)) {
        return false;
      }

      if (!matchesContextLocation(goal, context)) {
        return false;
      }

      if (goal.goalPeriodType !== periodType) {
        return false;
      }

      if (Number(goal.goalYear) !== contextYear) {
        return false;
      }

      if (periodType === 'monthly' && context.month) {
        return Number(goal.goalMonth) === Number(context.month);
      }

      if (periodType === 'weekly' && context.week) {
        return Number(goal.goalWeek) === Number(context.week);
      }

      if (periodType === 'quarterly' && context.quarter) {
        return Number(goal.goalQuarter) === Number(context.quarter);
      }

      return true;
    });

    return fallbackMatches.length
      ? [...fallbackMatches].sort(compareCurrentGoalPriority)[0]
      : null;
  }

  return null;
}

function resolveYearlyFallbackGoalRecord(goals = [], kpiKey, context = {}) {
  const contextYear = inferContextYear(context);
  const candidates = goals.filter(goal => {
    if (goal.kpiKey !== kpiKey) {
      return false;
    }

    if (!matchesContextLocation(goal, context)) {
      return false;
    }

    if (goal.goalPeriodType !== 'yearly') {
      return false;
    }

    if (!isCurrentActiveGoal(goal)) {
      return false;
    }

    if (contextYear !== null && Number(goal.goalYear) !== contextYear) {
      return false;
    }

    return true;
  });

  if (!candidates.length) {
    return null;
  }

  return [...candidates].sort((left, right) => (
    Number(right.goalYear || 0) - Number(left.goalYear || 0)
    || String(right.periodStart || '').localeCompare(String(left.periodStart || ''))
    || compareCurrentGoalPriority(left, right)
  ))[0];
}

function buildGoalScorecardRows({ kpis = [], goals = [], trackerMetrics = [], actuals = [], context = {} }) {
  const trackerMap = new Map();
  trackerMetrics.forEach(metric => {
    trackerMap.set(metric.kpiKey, metric);
  });
  const actualSummaryByKpiId = summarizeActualRowsByKpi({ kpis, actuals });

  return getVisiblePerformanceKpis(kpis, context)
    .map(kpi => {
      const exactGoalRecord = resolveExactGoalRecord(goals, kpi.kpiKey, context);
      const fallbackGoalRecord = exactGoalRecord
        ? null
        : resolveYearlyFallbackGoalRecord(goals, kpi.kpiKey, context);
      const trackerMetric = trackerMap.get(kpi.kpiKey) || null;
      const targetSource = exactGoalRecord
        ? 'exact'
        : fallbackGoalRecord
          ? 'yearly_reference'
          : 'missing';
      const targetDisplayValue = exactGoalRecord?.goalValue
        ?? fallbackGoalRecord?.goalValue
        ?? trackerMetric?.goal
        ?? null;
      const target = targetDisplayValue;
      const actual = actualSummaryByKpiId.get(Number(kpi.id)) ?? trackerMetric?.actual ?? null;
      const variance = Number.isFinite(target) && Number.isFinite(actual) ? actual - target : null;

      return {
        id: exactGoalRecord?.id || `new-${kpi.kpiKey}`,
        locationCode: exactGoalRecord?.locationCode || fallbackGoalRecord?.locationCode || context.locationCode || '',
        contextPeriodType: context.periodType || '',
        contextPeriodStart: context.periodStart || '',
        contextYear: inferContextYear(context),
        category: kpi.category || '',
        goalName: kpi.sectionName || kpi.section || 'General',
        kpiKey: kpi.kpiKey,
        kpiLabel: kpi.label || kpi.name || kpi.kpiKey,
        unit: kpi.unit || '',
        formatType: kpi.formatType || 'number',
        target,
        targetDisplayValue,
        targetSource,
        actual,
        variance,
        isLocked: Boolean(exactGoalRecord?.isLocked),
        canAllocate: Boolean(exactGoalRecord),
        goalRecord: exactGoalRecord,
        exactGoalRecord,
        fallbackGoalRecord
      };
    });
}

function summarizeActualRowsByKpi({ kpis = [], actuals = [] }) {
  const kpiById = new Map((kpis || []).map((kpi) => [Number(kpi.id), kpi]));
  const groupedValues = new Map();

  actuals.forEach((actual) => {
    const kpiDefinitionId = Number(actual?.kpiDefinitionId);
    const actualValue = Number(actual?.actualValue);

    if (!Number.isFinite(kpiDefinitionId) || !Number.isFinite(actualValue)) {
      return;
    }

    if (!groupedValues.has(kpiDefinitionId)) {
      groupedValues.set(kpiDefinitionId, []);
    }

    groupedValues.get(kpiDefinitionId).push(actualValue);
  });

  return Array.from(groupedValues.entries()).reduce((summary, [kpiDefinitionId, values]) => {
    const kpi = kpiById.get(kpiDefinitionId) || {};
    const aggregationMode = getAggregationMode(kpi);
    const resolvedValue = aggregationMode === 'sum'
      ? values.reduce((sum, value) => sum + value, 0)
      : values.reduce((sum, value) => sum + value, 0) / values.length;

    summary.set(kpiDefinitionId, resolvedValue);
    return summary;
  }, new Map());
}

function buildActualScorecardRows({ kpis = [], goals = [], actuals = [], trackerMetrics = [], managerName = '', context = {} }) {
  const actualMap = new Map();
  actuals.forEach(actual => {
    actualMap.set(actual.kpiDefinitionId, actual);
  });

  const trackerMap = new Map();
  trackerMetrics.forEach(metric => {
    trackerMap.set(metric.kpiDefinitionId, metric);
  });

  return getVisiblePerformanceKpis(kpis, context)
    .map(kpi => {
      const actual = actualMap.get(kpi.id) || null;
      const exactGoalRecord = resolveExactGoalRecord(goals, kpi.kpiKey, context);
      const fallbackGoalRecord = exactGoalRecord
        ? null
        : resolveYearlyFallbackGoalRecord(goals, kpi.kpiKey, context);
      const trackerMetric = trackerMap.get(kpi.id) || null;
      const trackerGoal = trackerMetric?.goal ?? null;
      const targetSource = exactGoalRecord
        ? 'exact'
        : fallbackGoalRecord
          ? 'yearly_reference'
          : trackerGoal !== null
            ? 'tracker_goal'
            : 'missing';
      const targetDisplayValue = exactGoalRecord?.goalValue
        ?? fallbackGoalRecord?.goalValue
        ?? trackerGoal;
      const target = targetDisplayValue;
      const actualValue = actual?.actualValue ?? trackerMetric?.actual ?? null;
      const variance = Number.isFinite(target) && Number.isFinite(actualValue) ? actualValue - target : null;

      return {
        id: actual?.id || `new-${kpi.id}`,
        managerId: actual?.managerId || null,
        managerName: actual?.managerName || managerName,
        category: kpi.category || '',
        goalName: kpi.sectionName || kpi.section || 'General',
        kpiDefinitionId: kpi.id,
        kpiKey: kpi.kpiKey,
        kpiLabel: kpi.label || kpi.name || kpi.kpiKey,
        unit: kpi.unit || '',
        formatType: kpi.formatType || 'number',
        target,
        targetDisplayValue,
        targetSource,
        actual: actualValue,
        variance,
        notes: actual?.notes || '',
        isLocked: Boolean(actual?.isLocked),
        actualRecord: actual,
        goalRecord: exactGoalRecord,
        exactGoalRecord,
        fallbackGoalRecord
      };
    });
}

function getPerformanceRowStatus(stage, row, options = {}) {
  if (stage === 'goals') {
    if (row?.targetSource === 'yearly_reference' && Number.isFinite(row?.targetDisplayValue) && !row?.goalRecord) {
      return {
        label: 'Yearly Reference Only',
        tone: 'not-required',
        detail: 'Create an exact target for this location and period'
      };
    }

    if (row?.isLocked) {
      return {
        label: 'Locked',
        tone: 'not-required',
        detail: 'Locked period'
      };
    }

    if (options.isDirty) {
      return {
        label: 'Unsaved Change',
        tone: 'needs-link',
        detail: 'Save target to apply'
      };
    }

    if (Number.isFinite(row?.target)) {
      return {
        label: 'Target Set',
        tone: 'linked',
        detail: 'Ready for allocation'
      };
    }

    return {
      label: 'Missing Target',
      tone: 'needs-link',
      detail: 'Set target to continue'
    };
  }

  if (stage === 'allocations') {
    if (row?.targetSource === 'yearly_reference' && Number.isFinite(row?.targetDisplayValue)) {
      return {
        label: 'Yearly Reference Only',
        tone: 'not-required',
        detail: 'Create an exact target in Goals'
      };
    }

    if (!row?.goalRecord || !Number.isFinite(row?.target)) {
      return {
        label: 'Missing Target',
        tone: 'needs-link',
        detail: 'Create target in Goals'
      };
    }

    if (options.isAllocated) {
      return {
        label: 'Allocated',
        tone: 'linked',
        detail: options.allocationCount
          ? `${options.allocationCount} manager assignment${options.allocationCount === 1 ? '' : 's'} on selected goal`
          : 'Selected goal allocated'
      };
    }

    return {
      label: 'Ready to Allocate',
      tone: 'not-required',
      detail: 'Exact target available for allocation'
    };
  }

  if (stage === 'actuals') {
    if (row?.targetSource === 'yearly_reference' && Number.isFinite(row?.targetDisplayValue) && !row?.goalRecord) {
      return {
        label: 'Yearly Reference Goal',
        tone: 'not-required',
        detail: 'Using yearly goal as reference for this KPI'
      };
    }

    if (!Number.isFinite(row?.target)) {
      return {
        label: 'Missing Target',
        tone: 'needs-link',
        detail: 'Set a goal in Goals first'
      };
    }

    if (row?.actualRecord) {
      return {
        label: 'Actual Entered',
        tone: 'linked',
        detail: row.isLocked ? 'Locked record' : 'Saved for this period'
      };
    }

    return {
      label: 'Awaiting Actual',
      tone: 'not-required',
      detail: 'Ready for entry'
    };
  }

  return {
    label: 'Open',
    tone: 'not-required',
    detail: ''
  };
}

const PERFORMANCE_LIFECYCLE_STATUSES = [
  'Missing Target',
  'Target Set',
  'Allocated',
  'Awaiting Actual',
  'Actual Entered'
];

function getPerformanceLifecycleStatus(stage, row, options = {}) {
  if (stage === 'goals') {
    return Number.isFinite(row?.target) ? 'Target Set' : 'Missing Target';
  }

  if (stage === 'allocations') {
    if (!row?.goalRecord || !Number.isFinite(row?.target)) {
      return 'Missing Target';
    }
    return options.isAllocated ? 'Allocated' : 'Target Set';
  }

  if (stage === 'actuals') {
    if (!Number.isFinite(row?.target)) {
      return 'Missing Target';
    }
    return row?.actualRecord ? 'Actual Entered' : 'Awaiting Actual';
  }

  return 'Missing Target';
}

function summarizePerformanceLifecycleCounts(rows = [], stage, getOptions = () => ({})) {
  return rows.reduce((counts, row) => {
    const status = getPerformanceLifecycleStatus(stage, row, getOptions(row));
    counts[status] += 1;
    return counts;
  }, PERFORMANCE_LIFECYCLE_STATUSES.reduce((accumulator, status) => {
    accumulator[status] = 0;
    return accumulator;
  }, {}));
}

export {
  buildActualScorecardRows,
  buildGoalScorecardRows,
  createDefaultPerformanceContext,
  alignPerformanceContextPeriod,
  derivePeriodStartFromContext,
  formatMetricValue,
  getPerformanceLifecycleStatus,
  getPerformanceRowStatus,
  getVisiblePerformanceKpis,
  normalizePeriodStartValue,
  PERFORMANCE_LIFECYCLE_STATUSES,
  summarizePerformanceLifecycleCounts
};
